<div class="p-6">
  <!-- Visually hidden: the tab bar already shows this label. See nyc-sync-page for rationale. -->
  <h1 class="sr-only">Users</h1>

  <!--
    Two groups, because these are two different jobs: narrowing the list down, and jumping straight
    to one person you can already name. They used to sit in one unlabelled eight-control row that
    could not wrap, so on a narrow window the phone lookup simply ran off the side of the page.
  -->
  <div class="flex flex-wrap items-end gap-x-6 gap-y-4">
    <div class="flex flex-wrap items-center gap-2">
      <p class="text-xs font-semibold uppercase tracking-wide text-muted-color w-full">Browse</p>
      <p-select
        [options]="viewOptions"
        [ngModel]="finalView()"
        (ngModelChange)="onModeChange($event)"
        optionLabel="label"
        optionValue="value"
        ariaLabel="View"
      ></p-select>
      <input
        pInputText
        [ngModel]="search()"
        (ngModelChange)="onSearchChange($event)"
        placeholder="Search"
        aria-label="Search users"
      />
    </div>

    <div class="flex flex-wrap items-center gap-2">
      <p class="text-xs font-semibold uppercase tracking-wide text-muted-color w-full">Find exact</p>
      <!-- Was a native prompt(): unstyled, outside the app's dialog conventions, and blockable by
           the browser. An inline field also lets the id be pasted and corrected in place. -->
      <input
        pInputText
        [ngModel]="idLookup()"
        (ngModelChange)="idLookup.set($event)"
        (keydown.enter)="openById()"
        placeholder="User ID"
        aria-label="Open by user ID"
      />
      <p-button (onClick)="openById()" icon="pi pi-user" label="Open" [disabled]="!idLookup().trim()"></p-button>

      <input
        pInputText
        [ngModel]="emailSearch()"
        (ngModelChange)="emailSearch.set($event)"
        (keydown.enter)="searchByEmail()"
        placeholder="Email"
        aria-label="Exact email search"
      />
      <p-button
        (onClick)="searchByEmail()"
        icon="pi pi-search"
        label="Email"
        [loading]="emailSearchLoading()"
        [disabled]="!emailSearch().trim()"
      ></p-button>

      <input
        pInputText
        [ngModel]="phoneSearch()"
        (ngModelChange)="phoneSearch.set($event)"
        (keydown.enter)="searchByPhone()"
        placeholder="Phone"
        aria-label="Search by phone"
      />
      <p-button
        (onClick)="searchByPhone()"
        icon="pi pi-search"
        label="Phone"
        [loading]="phoneSearchLoading()"
        [disabled]="!phoneSearch().trim()"
      ></p-button>
    </div>
  </div>

  <div class="flex flex-row gap-2 mt-4 mb-4 items-center" role="status" aria-live="polite" aria-atomic="true">
    @if (loading()) {
      <span>Loading…</span>
    } @else {
      <span>Showing {{ users().length }} of {{ totalCount() }}{{ truncated() ? '+' : '' }} users</span>
      @if (truncated()) {
        <span class="text-sm text-surface-500">Refine your search to see the rest.</span>
      }
    }
  </div>

  <!-- min-width instead of letting 10 columns share the viewport: below it the table scrolls
       sideways, which keeps every column readable rather than squeezing the narrow ones until an
       email address wraps one character per line. -->
  <p-table
    class="block"
    size="small"
    [value]="users()"
    [scrollable]="true"
    scrollHeight="65vh"
    [tableStyle]="{'min-width': '86rem'}"
  >
    <ng-template pTemplate="caption">
      <span class="sr-only">Users</span>
    </ng-template>
    <!-- Name first and id last: the id is the widest column and the least readable, and now that
         the name carries the link it is no longer the only way into a user. -->
    <ng-template pTemplate="header">
      <tr class="[&>th]:whitespace-nowrap">
        <th>Name</th>
        <th>Email</th>
        <th>Status</th>
        <th>Payment</th>
        <th>Sync</th>
        <th>Assistance</th>
        <th>Created at</th>
        <th>Last seen</th>
        <th><span class="sr-only">Flags</span></th>
        <th>Id</th>
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-user [homeyTypedTableRow]="users()">
      <!-- align-top: one long sync error otherwise leaves every other cell in the row floating in
           the middle of a band of whitespace. -->
      <tr class="align-top">
        <td class="whitespace-nowrap">
          <homey-user-link [id]="user.id" [label]="userDisplayName(user)"/>
        </td>
        <td class="max-w-56 truncate" [title]="user.email">{{ user.email }}</td>
        <td>
          <p-tag [severity]="homeyStatusToTag(user.status)">{{ user.status }}</p-tag>
        </td>
        <td>
          <p-tag [severity]="paymentStatusToTag(user.paymentStatus)">{{ user.paymentStatus }}</p-tag>
        </td>
        <td>
          @if (user.lastSyncStatus === 'sync-failed') {
            <div class="flex items-center gap-2">
              <p-tag severity="danger">FAILED</p-tag>
              <span class="text-xs max-w-48 truncate" [title]="user.lastSyncError ?? ''">
                {{ user.lastSyncError ?? 'No error detail' }}
              </span>
            </div>
          } @else if (user.lastSyncError) {
            <div class="flex items-center gap-2">
              <p-tag severity="warn">ERROR</p-tag>
              <span class="text-xs max-w-48 truncate" [title]="user.lastSyncError">{{ user.lastSyncError }}</span>
            </div>
          }
        </td>
        <!-- Was rendering the raw boolean as "true"/"false". -->
        <td>{{ user.rentalAssistance ? 'Yes' : '-' }}</td>
        <td class="whitespace-nowrap">{{ user.createdAt | date: dateTimeFormat }}</td>
        <td class="whitespace-nowrap">{{ user.lastSeen | date: dateTimeFormat }}</td>
        <td>
          @if (user.ignore) {
            <p-tag severity="danger">IGNORED</p-tag>
          }
        </td>
        <td class="font-mono text-xs text-muted-color whitespace-nowrap">{{ user.id }}</td>
      </tr>
    </ng-template>
    <!-- Without this an empty result renders as bare column headers, which reads as a broken
         request rather than an answer. -->
    <ng-template pTemplate="emptymessage">
      <tr>
        <td colspan="10" class="text-center">No users match this search</td>
      </tr>
    </ng-template>
  </p-table>
</div>
